// Life & Wealth Solutions - Phase 5: Enhanced Analytics Dashboard
// Phoenix Rescue Apps - "Your Agency to AI Today"
// Integrates with Phase 4 AI Analytics Service

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, Users,
  Calendar, Phone, Target, Award, AlertCircle,
  RefreshCw, Download, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AnalyticsDashboardEnhanced = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('30');
  const [dashboardData, setDashboardData] = useState(null);
  const [leadMetrics, setLeadMetrics] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Fetch all analytics endpoints from Phase 4 AI Analytics Service
      const [dashboard, leads, revenue, performance] = await Promise.all([
        axios.get(`${API_URL}/analytics/dashboard?days=${timeRange}`, config),
        axios.get(`${API_URL}/analytics/leads?days=${timeRange}`, config),
        axios.get(`${API_URL}/analytics/revenue?days=${timeRange}`, config),
        axios.get(`${API_URL}/analytics/performance?days=${timeRange}`, config)
      ]);

      setDashboardData(dashboard.data);
      setLeadMetrics(leads.data);
      setRevenueData(revenue.data);
      setPerformanceData(performance.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Set demo data for testing
      setDemoData();
    } finally {
      setLoading(false);
    }
  };

  const setDemoData = () => {
    setDashboardData({
      metrics: {
        total_leads: 1247,
        active_leads: 423,
        conversion_rate: 24.5,
        total_appointments: 287,
        completed_appointments: 245,
        appointment_completion_rate: 85.4,
        total_tasks: 892,
        completed_tasks: 734,
        task_completion_rate: 82.3,
        total_calls: 1456,
        successful_calls: 987,
        call_success_rate: 67.8,
        average_response_time: 4.2,
        revenue_current: 487500,
        revenue_previous: 423000,
        revenue_growth: 15.3
      },
      ai_insights: [
        'Lead conversion rate improved by 8% this month',
        'Thursday shows highest appointment completion rate at 91%',
        'Email campaigns generating 35% higher quality leads',
        'Agent Sarah has highest conversion rate at 34%'
      ],
      performance_score: 87
    });

    setLeadMetrics({
      by_source: [
        { source: 'Website', count: 456, percentage: 36.6 },
        { source: 'Referral', count: 324, percentage: 26.0 },
        { source: 'Social Media', count: 267, percentage: 21.4 },
        { source: 'Email Campaign', count: 145, percentage: 11.6 },
        { source: 'Other', count: 55, percentage: 4.4 }
      ],
      by_status: [
        { status: 'new', count: 234, percentage: 18.8 },
        { status: 'contacted', count: 389, percentage: 31.2 },
        { status: 'qualified', count: 312, percentage: 25.0 },
        { status: 'proposal', count: 178, percentage: 14.3 },
        { status: 'closed_won', count: 134, percentage: 10.7 }
      ],
      conversion_funnel: [
        { stage: 'New Leads', count: 1247, percentage: 100 },
        { stage: 'Contacted', count: 934, percentage: 74.9 },
        { stage: 'Qualified', count: 623, percentage: 50.0 },
        { stage: 'Proposal', count: 398, percentage: 31.9 },
        { stage: 'Closed Won', count: 306, percentage: 24.5 }
      ],
      average_conversion_time: 18.5
    });

    setRevenueData({
      total: 487500,
      previous_period: 423000,
      growth_rate: 15.3,
      by_period: [
        { period: 'Week 1', amount: 98500 },
        { period: 'Week 2', amount: 112300 },
        { period: 'Week 3', amount: 134700 },
        { period: 'Week 4', amount: 142000 }
      ],
      by_product: [
        { product: 'Term Life', amount: 187500, percentage: 38.5 },
        { product: 'Whole Life', amount: 156200, percentage: 32.0 },
        { product: 'Universal Life', amount: 87600, percentage: 18.0 },
        { product: 'Annuities', amount: 56200, percentage: 11.5 }
      ],
      forecast_3_month: 1542000,
      forecast_6_month: 3187000,
      forecast_confidence: 0.87
    });

    setPerformanceData({
      overall_score: 87,
      trends: [
        { date: '2025-11-11', score: 78 },
        { date: '2025-11-18', score: 81 },
        { date: '2025-11-25', score: 84 },
        { date: '2025-12-02', score: 85 },
        { date: '2025-12-09', score: 87 }
      ],
      by_metric: {
        lead_response: 92,
        conversion_rate: 85,
        customer_satisfaction: 89,
        appointment_adherence: 91,
        task_completion: 82
      }
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalyticsData();
    setRefreshing(false);
  };

  const handleExport = () => {
    // Export analytics data as CSV
    const csvData = generateCSVData();
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const generateCSVData = () => {
    if (!dashboardData) return '';
    
    let csv = 'Metric,Value\n';
    csv += `Total Leads,${dashboardData.metrics.total_leads}\n`;
    csv += `Conversion Rate,${dashboardData.metrics.conversion_rate}%\n`;
    csv += `Revenue,$${dashboardData.metrics.revenue_current}\n`;
    csv += `Performance Score,${dashboardData.performance_score}\n`;
    
    return csv;
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const MetricCard = ({ icon: Icon, title, value, change, trend }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-50 rounded-lg">
            <Icon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
        {change && (
          <div className={`flex items-center space-x-1 ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
            <span className="text-sm font-medium">{change}%</span>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500 mt-1">AI-Powered Business Intelligence & Insights</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="365">Last Year</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {['overview', 'leads', 'revenue', 'performance'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              icon={Users}
              title="Total Leads"
              value={dashboardData?.metrics.total_leads || 0}
              change={12.5}
              trend="up"
            />
            <MetricCard
              icon={Target}
              title="Conversion Rate"
              value={`${dashboardData?.metrics.conversion_rate || 0}%`}
              change={8.3}
              trend="up"
            />
            <MetricCard
              icon={DollarSign}
              title="Revenue"
              value={`$${(dashboardData?.metrics.revenue_current || 0).toLocaleString()}`}
              change={dashboardData?.metrics.revenue_growth || 0}
              trend="up"
            />
            <MetricCard
              icon={Award}
              title="Performance Score"
              value={dashboardData?.performance_score || 0}
              change={5.2}
              trend="up"
            />
          </div>

          {/* AI Insights */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow border border-blue-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">AI-Powered Insights</h2>
            </div>
            <div className="space-y-2">
              {dashboardData?.ai_insights?.map((insight, idx) => (
                <div key={idx} className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">{insight}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue Trend</h2>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData?.by_period || []}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Area type="monotone" dataKey="amount" stroke="#3B82F6" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Performance Score Trend */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Performance Trend</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData?.trends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString()} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="score" stroke="#10B981" strokeWidth={2} name="Performance Score" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lead Sources */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Leads by Source</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={leadMetrics?.by_source || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ source, percentage }) => `${source} ${percentage.toFixed(1)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {(leadMetrics?.by_source || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Lead Status Distribution */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Lead Status Distribution</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={leadMetrics?.by_status || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Conversion Funnel</h2>
            <div className="space-y-4">
              {leadMetrics?.conversion_funnel?.map((stage, idx) => (
                <div key={idx} className="flex items-center">
                  <div className="w-32 text-sm font-medium text-gray-700">{stage.stage}</div>
                  <div className="flex-1 bg-gray-200 rounded-full h-10 relative">
                    <div
                      className={`h-10 rounded-full flex items-center justify-between px-4 text-white text-sm font-medium ${
                        idx === 0 ? 'bg-blue-600' :
                        idx === 1 ? 'bg-blue-500' :
                        idx === 2 ? 'bg-blue-400' :
                        idx === 3 ? 'bg-green-500' :
                        'bg-green-600'
                      }`}
                      style={{ width: `${stage.percentage}%` }}
                    >
                      <span>{stage.count} leads</span>
                      <span>{stage.percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Average Conversion Time:</span> {leadMetrics?.average_conversion_time} days
              </p>
            </div>
          </div>
        </>
      )}

      {/* Revenue Tab */}
      {activeTab === 'revenue' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard
              icon={DollarSign}
              title="Total Revenue"
              value={`$${(revenueData?.total || 0).toLocaleString()}`}
              change={revenueData?.growth_rate || 0}
              trend="up"
            />
            <MetricCard
              icon={TrendingUp}
              title="3-Month Forecast"
              value={`$${(revenueData?.forecast_3_month || 0).toLocaleString()}`}
            />
            <MetricCard
              icon={TrendingUp}
              title="6-Month Forecast"
              value={`$${(revenueData?.forecast_6_month || 0).toLocaleString()}`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue by Product */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue by Product</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData?.by_product || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="product" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="amount" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Revenue Growth */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Weekly Revenue Growth</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueData?.by_period || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Line type="monotone" dataKey="amount" stroke="#8B5CF6" strokeWidth={2} name="Revenue" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Forecast Confidence */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue Forecast</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">3-Month Forecast</span>
                    <span className="text-lg font-bold text-gray-900">
                      ${(revenueData?.forecast_3_month || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${(revenueData?.forecast_confidence || 0) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {((revenueData?.forecast_confidence || 0) * 100).toFixed(0)}% confidence
                  </p>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">6-Month Forecast</span>
                    <span className="text-lg font-bold text-gray-900">
                      ${(revenueData?.forecast_6_month || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${((revenueData?.forecast_confidence || 0) - 0.15) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {(((revenueData?.forecast_confidence || 0) - 0.15) * 100).toFixed(0)}% confidence
                  </p>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Forecast Insights</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Based on historical performance and current trends
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Accounts for seasonal variations
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Updated daily with AI/ML models
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {Object.entries(performanceData?.by_metric || {}).map(([metric, score]) => (
              <div key={metric} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2 capitalize">
                  {metric.replace(/_/g, ' ')}
                </h3>
                <div className="flex items-end space-x-2">
                  <span className="text-3xl font-bold text-gray-900">{score}</span>
                  <span className="text-gray-500 mb-1">/100</span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      score >= 90 ? 'bg-green-600' :
                      score >= 75 ? 'bg-blue-600' :
                      score >= 60 ? 'bg-yellow-600' :
                      'bg-red-600'
                    }`}
                    style={{ width: `${score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Overall Performance Score</h2>
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-5xl font-bold text-gray-900">{performanceData?.overall_score || 0}</p>
                <p className="text-gray-500 mt-1">Overall Performance</p>
              </div>
              <div className="w-32 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { value: performanceData?.overall_score || 0 },
                        { value: 100 - (performanceData?.overall_score || 0) }
                      ]}
                      cx="50%"
                      cy="50%"
                      startAngle={90}
                      endAngle={-270}
                      innerRadius={40}
                      outerRadius={60}
                      dataKey="value"
                    >
                      <Cell fill="#10B981" />
                      <Cell fill="#E5E7EB" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceData?.trends || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString()} />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="score" stroke="#10B981" strokeWidth={3} name="Performance Score" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboardEnhanced;
